import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  IconButton,
  Alert,
  Stack,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Api as ApiIcon,
  Code as CodeIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PriorityHigh as PriorityHighIcon,
  AccessTime as TimeIcon,
  Pending as PendingIcon,
  PlayArrow as PlayArrowIcon,
  Block as BlockIcon,
  CheckCircleOutline as ApproveIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import type { EndpointRequest, HttpMethod, Priority, RequestStatus } from "../types";

interface ViewRequestDialogProps {
  open: boolean;
  request: EndpointRequest;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<EndpointRequest>) => void;
  onDelete: (id: string) => void;
}

const HTTP_METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const PRIORITIES: Priority[] = ["low", "medium", "high", "urgent"];
const STATUSES: RequestStatus[] = ["pending", "in_progress", "completed", "blocked", "cancelled"];

const METHOD_COLORS: Record<HttpMethod, string> = {
  GET: "#4caf50",
  POST: "#2196f3",
  PUT: "#ff9800",
  PATCH: "#9c27b0",
  DELETE: "#f44336",
};

const PRIORITY_COLORS: Record<Priority, "error" | "warning" | "info" | "default"> = {
  urgent: "error",
  high: "warning",
  medium: "info",
  low: "default",
};

const getStatusIcon = (status: RequestStatus) => {
  switch (status) {
    case "completed":
      return <CheckCircleIcon fontSize="small" />;
    case "in_progress":
      return <PlayArrowIcon fontSize="small" />;
    case "pending":
      return <PendingIcon fontSize="small" />;
    case "blocked":
      return <BlockIcon fontSize="small" />;
    case "cancelled":
      return <CancelIcon fontSize="small" />;
  }
};

export const ViewRequestDialog: React.FC<ViewRequestDialogProps> = ({
  open,
  request,
  onClose,
  onUpdate,
  onDelete,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";
  // Check if user is backend dev - check permissions or username for dev indicators
  const isBackendDev = 
    user?.permissions?.some((perm: string) => 
      perm.toLowerCase().includes("dev") || 
      perm.toLowerCase().includes("backend") ||
      perm.toLowerCase().includes("configuration")
    ) ||
    user?.username?.toLowerCase().includes("dev") ||
    user?.username?.toLowerCase().includes("backend") ||
    false;

  const [isEditing, setIsEditing] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "timeline">("details");
  const [formData, setFormData] = useState<EndpointRequest>(request);
  const [requestDate, setRequestDate] = useState<Dayjs | null>(
    request.requestDate ? dayjs(request.requestDate) : dayjs()
  );
  const [targetDate, setTargetDate] = useState<Dayjs | null>(
    request.targetDeliveryDate ? dayjs(request.targetDeliveryDate) : null
  );
  const [expectedDate, setExpectedDate] = useState<Dayjs | null>(
    request.expectedDeliveryDate ? dayjs(request.expectedDeliveryDate) : null
  );
  const [actualDate, setActualDate] = useState<Dayjs | null>(
    request.actualCompletionDate ? dayjs(request.actualCompletionDate) : null
  );

  React.useEffect(() => {
    if (open) {
      setFormData(request);
      setRequestDate(request.requestDate ? dayjs(request.requestDate) : dayjs());
      setTargetDate(request.targetDeliveryDate ? dayjs(request.targetDeliveryDate) : null);
      setExpectedDate(request.expectedDeliveryDate ? dayjs(request.expectedDeliveryDate) : null);
      setActualDate(request.actualCompletionDate ? dayjs(request.actualCompletionDate) : null);
      setIsEditing(false);
      setIsApproving(false);
    }
  }, [request, open]);

  const handleSave = () => {
    if (!formData.component || !formData.task || !formData.lead) {
      alert("Please fill in all required fields");
      return;
    }

    const updates: Partial<EndpointRequest> = {
      ...formData,
      requestDate: requestDate?.format("YYYY-MM-DD") || formData.requestDate,
      targetDeliveryDate: targetDate?.format("YYYY-MM-DD") || undefined,
      expectedDeliveryDate: expectedDate?.format("YYYY-MM-DD") || undefined,
      actualCompletionDate: actualDate?.format("YYYY-MM-DD") || undefined,
    };

    onUpdate(request.id!, updates);
    setIsEditing(false);
    setIsApproving(false);
  };

  const handleApprove = () => {
    if (!formData.endpoint) {
      alert("Please assign an endpoint URL before approving");
      return;
    }

    const updates: Partial<EndpointRequest> = {
      ...formData,
      status: "completed",
      actualCompletionDate: new Date().toISOString().split("T")[0],
      approvedBy: user?.username || "Unknown",
      approvedAt: new Date().toISOString(),
    };

    onUpdate(request.id!, updates);
    setIsApproving(false);
  };

  const handleCancel = () => {
    setFormData(request);
    setRequestDate(request.requestDate ? dayjs(request.requestDate) : dayjs());
    setTargetDate(request.targetDeliveryDate ? dayjs(request.targetDeliveryDate) : null);
    setExpectedDate(request.expectedDeliveryDate ? dayjs(request.expectedDeliveryDate) : null);
    setActualDate(request.actualCompletionDate ? dayjs(request.actualCompletionDate) : null);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this request?")) {
      onDelete(request.id!);
    }
  };

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case "completed":
        return "success";
      case "in_progress":
        return "info";
      case "pending":
        return "warning";
      case "blocked":
        return "error";
      case "cancelled":
        return "default";
    }
  };

  const getStatusProgress = (status: RequestStatus) => {
    switch (status) {
      case "completed":
        return 100;
      case "in_progress":
        return 50;
      case "pending":
        return 25;
      case "blocked":
        return 0;
      case "cancelled":
        return 0;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: primaryColor,
          color: "white",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}>
              <ApiIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {formData.component}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Endpoint Request Details
              </Typography>
            </Box>
          </Box>
          <Box>
            {isBackendDev && !isEditing && !isApproving && formData.status !== "completed" && (
              <Button
                variant="contained"
                startIcon={<ApproveIcon />}
                onClick={() => setIsApproving(true)}
                sx={{
                  mr: 1,
                  bgcolor: "success.main",
                  "&:hover": { bgcolor: "success.dark" },
                }}
              >
                Approve & Assign Endpoint
              </Button>
            )}
            {!isEditing && !isApproving ? (
              <>
                {!isBackendDev && (
                  <IconButton
                    onClick={() => setIsEditing(true)}
                    size="small"
                    sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                <IconButton
                  onClick={handleDelete}
                  size="small"
                  sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
                >
                  <DeleteIcon />
                </IconButton>
              </>
            ) : (
              <>
                <IconButton
                  onClick={handleSave}
                  size="small"
                  sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
                >
                  <SaveIcon />
                </IconButton>
                <IconButton
                  onClick={() => {
                    setIsEditing(false);
                    setIsApproving(false);
                    handleCancel();
                  }}
                  size="small"
                  sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
                >
                  <CancelIcon />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* Status Progress Bar */}
          <Box sx={{ px: 3, pt: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress
              </Typography>
              <Chip
                icon={getStatusIcon(formData.status)}
                label={formData.status.replace("_", " ").toUpperCase()}
                color={getStatusColor(formData.status) as any}
                size="small"
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={getStatusProgress(formData.status)}
              sx={{ height: 8, borderRadius: 1 }}
              color={getStatusColor(formData.status) as any}
            />
          </Box>

          {/* Error Alert */}
          {request.statusCode && request.statusCode >= 400 && (
            <Alert
              severity="error"
              icon={<ErrorIcon />}
              sx={{ m: 3, mb: 0 }}
              action={
                <Chip
                  label={`${request.statusCode}`}
                  size="small"
                  color="error"
                  sx={{ fontWeight: "bold" }}
                />
              }
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Error Detected
              </Typography>
              <Typography variant="body2">{request.challenges || "An error occurred"}</Typography>
            </Alert>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, mt: 2 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
              <Tab label="Details" value="details" />
              <Tab label="Timeline" value="timeline" />
            </Tabs>
          </Box>

          <Stack spacing={3} sx={{ p: 3 }}>
            {activeTab === "details" ? (
              <>
                {/* Key Information Cards */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Component
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            value={formData.component}
                            onChange={(e) => setFormData({ ...formData, component: e.target.value })}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        ) : (
                          <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5 }}>
                            {formData.component}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Lead Developer
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            value={formData.lead}
                            onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
                              {formData.lead.charAt(0)}
                            </Avatar>
                            <Typography variant="h6" fontWeight="bold">
                              {formData.lead}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Task Description */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Task Description
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={formData.task}
                        onChange={(e) => setFormData({ ...formData, task: e.target.value })}
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                        {formData.task}
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Endpoint & Method */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Endpoint URL
                          {isBackendDev && !formData.endpoint && (
                            <Chip
                              label="Assign Endpoint"
                              size="small"
                              color="warning"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                        {(isEditing || isApproving || (isBackendDev && !formData.endpoint)) ? (
                          <TextField
                            fullWidth
                            value={formData.endpoint || ""}
                            onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })}
                            size="small"
                            sx={{ mt: 1 }}
                            placeholder="/api/endpoint/path/"
                            required={isApproving}
                            InputProps={{
                              startAdornment: <CodeIcon sx={{ mr: 1, opacity: 0.5 }} />,
                            }}
                          />
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                            <CodeIcon sx={{ opacity: 0.5 }} />
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: "monospace", fontWeight: "medium" }}
                            >
                              {formData.endpoint || "Not assigned yet"}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          HTTP Method
                        </Typography>
                        {isEditing ? (
                          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <Select
                              value={formData.method || "GET"}
                              onChange={(e) =>
                                setFormData({ ...formData, method: e.target.value as HttpMethod })
                              }
                            >
                              {HTTP_METHODS.map((method) => (
                                <MenuItem key={method} value={method}>
                                  {method}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            label={formData.method || "N/A"}
                            sx={{
                              mt: 1,
                              backgroundColor: METHOD_COLORS[formData.method as HttpMethod] || "#666",
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Status, Priority, Dates */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Status
                        </Typography>
                        {(isEditing || (isBackendDev && isApproving)) ? (
                          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <Select
                              value={formData.status}
                              onChange={(e) =>
                                setFormData({ ...formData, status: e.target.value as RequestStatus })
                              }
                              disabled={isApproving}
                            >
                              {STATUSES.map((status) => (
                                <MenuItem key={status} value={status}>
                                  {status.replace("_", " ").toUpperCase()}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            icon={getStatusIcon(formData.status)}
                            label={formData.status.replace("_", " ").toUpperCase()}
                            color={getStatusColor(formData.status) as any}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Priority
                        </Typography>
                        {isEditing ? (
                          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <Select
                              value={formData.priority}
                              onChange={(e) =>
                                setFormData({ ...formData, priority: e.target.value as Priority })
                              }
                            >
                              {PRIORITIES.map((priority) => (
                                <MenuItem key={priority} value={priority}>
                                  {priority.toUpperCase()}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            icon={<PriorityHighIcon />}
                            label={formData.priority.toUpperCase()}
                            color={PRIORITY_COLORS[formData.priority]}
                            variant="outlined"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Request Date
                        </Typography>
                        {isEditing ? (
                          <DatePicker
                            value={requestDate}
                            onChange={(newValue: any) => setRequestDate(newValue as Dayjs | null)}
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                sx: { mt: 1 },
                              },
                            }}
                          />
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {new Date(formData.requestDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Status Code
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 1 }}>
                          {formData.statusCode || "N/A"}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Dates Section */}
                {isEditing && (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <DatePicker
                        label="Target Delivery Date"
                        value={targetDate}
                        onChange={(newValue: any) => setTargetDate(newValue as Dayjs | null)}
                        slotProps={{
                          textField: { fullWidth: true },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <DatePicker
                        label="Expected Delivery Date"
                        value={expectedDate}
                        onChange={(newValue: any) => setExpectedDate(newValue as Dayjs | null)}
                        slotProps={{
                          textField: { fullWidth: true },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <DatePicker
                        label="Actual Completion Date"
                        value={actualDate}
                        onChange={(newValue: any) => setActualDate(newValue as Dayjs | null)}
                        slotProps={{
                          textField: { fullWidth: true },
                        }}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Challenges */}
                {formData.challenges && (
                  <Alert severity="warning" icon={<InfoIcon />}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Challenges / Issues
                    </Typography>
                    <Typography variant="body2">{formData.challenges}</Typography>
                  </Alert>
                )}

                {/* Payload */}
                {formData.payload && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Payload / Request Body
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          p: 2,
                          backgroundColor: "grey.100",
                          borderRadius: 1,
                          fontFamily: "monospace",
                          fontSize: "0.875rem",
                          maxHeight: 200,
                          overflow: "auto",
                        }}
                      >
                        <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{formData.payload}</pre>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Description */}
                {formData.description && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Additional Description
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={formData.description}
                          onChange={(e) =>
                            setFormData({ ...formData, description: e.target.value })
                          }
                          sx={{ mt: 1 }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                          {formData.description}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Challenges Input (Editing) */}
                {isEditing && (
                  <TextField
                    fullWidth
                    label="Challenges / Issues"
                    multiline
                    rows={3}
                    value={formData.challenges || ""}
                    onChange={(e) => setFormData({ ...formData, challenges: e.target.value })}
                    placeholder="Document any challenges or blockers..."
                  />
                )}
              </>
            ) : (
              /* Timeline Tab */
              <Box>
                <Stack spacing={2}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          <TimeIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Request Created
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(formData.requestDate).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                  {formData.targetDeliveryDate && (
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar sx={{ bgcolor: "info.main" }}>
                            <CalendarIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Target Delivery Date
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(formData.targetDeliveryDate).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                  {formData.actualCompletionDate && (
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar sx={{ bgcolor: "success.main" }}>
                            <CheckCircleIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Completed
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(formData.actualCompletionDate).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose}>Close</Button>
        {isApproving && (
          <Button onClick={handleApprove} variant="contained" color="success" startIcon={<ApproveIcon />}>
            Approve & Mark Complete
          </Button>
        )}
        {isEditing && (
          <Button onClick={handleSave} variant="contained" color="primary">
            Save Changes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
